// Bounding rects of the non-blank text nodes under `element`. Text nodes rather than a range
// over child elements, since a block or flex-item child reports its container's width, not its text's.
function* textRects(element) {
  let walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  let range = document.createRange();

  while (walker.nextNode()) {
    range.selectNodeContents(walker.currentNode);

    let rect = range.getBoundingClientRect();

    if (rect.width) yield rect;
  }
}

function textWidth(element) {
  let rects = [...textRects(element)];

  if (!rects.length) return 0;

  return Math.max(...rects.map(rect => rect.right)) - Math.min(...rects.map(rect => rect.left));
}

// The element's content box: the border-box rect inset by padding and border.
function contentBox(element) {
  let { borderLeftWidth, borderRightWidth, paddingLeft, paddingRight } = getComputedStyle(element);
  let rect = element.getBoundingClientRect();

  return {
    left: rect.left + parseFloat(borderLeftWidth) + parseFloat(paddingLeft),
    right: rect.right - parseFloat(borderRightWidth) - parseFloat(paddingRight),
  };
}

// True when a rendered rect sticks out past `box`, with half a pixel of tolerance.
function sticksOut(rect, { left, right }) {
  if (!rect.width && !rect.height) return false;

  return rect.left < left - 0.5 || rect.right > right + 0.5;
}

// True when a descendant sticks out past the content box. Descendants rather than
// scrollWidth, so overflow from absolutely positioned pseudo-elements doesn't count.
function overflows(element) {
  let box = contentBox(element);

  return [...element.querySelectorAll('*')].some(child =>
    sticksOut(child.getBoundingClientRect(), box)
  );
}

// True when a fitted width made the layout worse: the text rewrapped taller, or
// non-text content (images, inline-block boxes) now overflows.
function changedLayout(element, height) {
  return element.getBoundingClientRect().height - height > 0.5 || overflows(element);
}

// Undoes a previous fit before measuring. An authored box-sizing belongs to the element and
// changes which box max-width applies to, so only the content-box this module writes is
// cleared. A stale width is always ours — the element's own width lives in the stylesheet.
function clearFit(element) {
  let refit = element.style.boxSizing === 'content-box' && element.style.width;

  element.style.removeProperty('width');
  if (refit) element.style.removeProperty('box-sizing');
}

// Drops a fit that made the layout worse, restoring the box-sizing it replaced.
function undoFit(element, boxSizing) {
  element.style.removeProperty('width');

  if (boxSizing) element.style.boxSizing = boxSizing;
  else element.style.removeProperty('box-sizing');
}

// Narrows `element` to the widest line its text wraps to. CSS sizes a wrapped box to its
// max-width, not to its longest line. The element must be rendered and laid out. The fit is
// measured from text only, and dropped when it would rewrap or overflow non-text content.
export function shrinkWrap(element) {
  clearFit(element);

  let width = textWidth(element);
  let { left, right } = contentBox(element);

  if (!width || right - left - width < 1) return;

  let { height } = element.getBoundingClientRect();
  let { boxSizing } = element.style;

  element.style.boxSizing = 'content-box';
  element.style.width = `${Math.ceil(width)}px`;

  if (changedLayout(element, height)) undoFit(element, boxSizing);
}

export default { shrinkWrap };
